import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  BarChart3,
  Activity,
  RefreshCw
} from "lucide-react";
import { DevisProvider, useDevis } from "./context/DevisContext";
import DevisTable from "./components/DevisTable";
import DevisCardList from "./components/DevisCardList";
import EmptyState from "./components/EmptyState";
import SearchBar from "./components/SearchBar";
// import ItemsPerPageSelector from "./components/ItemsPerPageSelector";
import Pagination from "./components/Pagination";
import QuoteModals from "./components/QuoteModals";
// import BulkActions from "./components/BulkActions";

// Modern loading component for table area
const TableLoadingState = () => (
  <div className="flex items-center justify-center py-20">
    <div className="flex flex-col items-center space-y-6">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-green-200 rounded-full animate-spin"></div>
        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-green-600 rounded-full animate-spin"></div>
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-gray-700 mb-2">Loading quotes...</p>
        <p className="text-sm text-gray-500">Fetching data...</p>
      </div>
    </div>
  </div>
);

// Main component content (to be wrapped with provider)
const DevisSectionContent = () => {
  const { loading, error, filteredQuotes, fetchQuotes, isMobile, quotes } = useDevis();
  const [isVisible, setIsVisible] = useState(false);

  // Initial data fetch
  useEffect(() => {
    const checkAuthentication = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("You are not logged in. Please log in to access this page.");
        return false;
      }
      return true;
    };

    if (checkAuthentication()) {
      fetchQuotes();
    }
    setIsVisible(true);
  }, [fetchQuotes]);

  // Calculate stats
  const stats = {
    total: quotes.length,
    pending: quotes.filter(q => q.status === 'pending').length,
    confirmed: quotes.filter(q => q.status === 'confirmed').length,
    paid: quotes.filter(q => q.status === 'paid').length,
    rejected: quotes.filter(q => q.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-8`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Quotes Management</h1>
                  <p className="text-gray-600 mt-1">Administer customer quote requests</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-2">
                  <Activity className="w-4 h-4" />
                  <span>Live Data</span>
                </div>
                <button
                  onClick={fetchQuotes}
                  className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                  <div className="flex items-center space-x-2 relative z-10">
                    <RefreshCw className="w-5 h-5" />
                    <span>Refresh</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Quotes</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Confirmed</p>
                <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Paid</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.paid}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Rejected</p>
                <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <XCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Controls */}
        <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-8`}>
          <SearchBar placeholder="Search quotes..." />
        </div>

        {/* Error Alert */}
        {error && (
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-8`}>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-6 w-6 text-red-500 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Loading Error</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        {/* <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-8`}>
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <ItemsPerPageSelector />
            </div>
            <div className="flex-1">
              <BulkActions />
            </div>
          </div>
        </div> */}

        {/* Main Content */}
        <div className={`transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-8`}>
          {loading ? (
            <TableLoadingState />
          ) : (
            <>
              {filteredQuotes.length === 0 ? (
                <EmptyState message="No quotes found" />
              ) : isMobile ? (
                <DevisCardList />
              ) : (
                <DevisTable />
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        <div className={`transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Pagination />
        </div>

        {/* Modals */}
        <QuoteModals />
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

// Wrapper component with provider
const DevisSection = () => {
  return (
    <DevisProvider>
      <DevisSectionContent />
    </DevisProvider>
  );
};

export default DevisSection;
